import { useSeoTags } from "../useSeoTags";
import "../marketMyths.css";
import "../humanArticleTemplate.css";

// WAVE 1, 2026-10-28 -- humanized rewrite of the index-direction evidence
// doc (the largest, most complex piece in the batch -- 4 investigations +
// a case study + a positive control). Isolated, unlinked, noindexed
// preview. Every number below re-verified against the already-committed
// docs/research/nse_index_direction_nonforecastability.md, which is
// itself the project's sourced, reproducible record (script provenance
// in the full-numbers block) -- not re-derived here, since that doc IS
// the committed source.
//
// CHART: the Section-5 case-study table (our own earlier model's claimed
// accuracy vs. a model-free naive baseline, all 16 indices) is reused
// verbatim -- the single most dramatic, genuinely tension-carrying visual
// in the whole piece, and it needs no new computation at all.

const V8_AUDIT: { index: string; reported: number; naive: number; beatsNaive: boolean }[] = [
  { index: "NIFTY MEDIA", reported: 73.0, naive: 70.5, beatsNaive: true },
  { index: "NIFTY FMCG", reported: 70.1, naive: 74.8, beatsNaive: false },
  { index: "NIFTY FIN SERVICES", reported: 69.7, naive: 68.3, beatsNaive: true },
  { index: "BANK NIFTY", reported: 67.8, naive: 68.3, beatsNaive: false },
  { index: "NIFTY 50", reported: 67.2, naive: 67.9, beatsNaive: false },
  { index: "NIFTY CONSUMPTION", reported: 66.0, naive: 71.8, beatsNaive: false },
  { index: "NIFTY ENERGY", reported: 64.4, naive: 67.2, beatsNaive: false },
  { index: "NIFTY COMMODITIES", reported: 63.7, naive: 66.4, beatsNaive: false },
  { index: "NIFTY REALTY", reported: 63.2, naive: 63.2, beatsNaive: false },
  { index: "NIFTY PHARMA", reported: 60.8, naive: 58.7, beatsNaive: true },
  { index: "NIFTY MNC", reported: 60.7, naive: 69.4, beatsNaive: false },
  { index: "NIFTY INFRA", reported: 60.2, naive: 63.7, beatsNaive: false },
  { index: "NIFTY IT", reported: 59.5, naive: 62.5, beatsNaive: false },
  { index: "NIFTY METAL", reported: 59.1, naive: 54.7, beatsNaive: true },
  { index: "NIFTY PSU BANK", reported: 59.1, naive: 54.0, beatsNaive: true },
  { index: "NIFTY AUTO", reported: 55.8, naive: 61.2, beatsNaive: false },
];

function V8AuditChart() {
  const width = 680;
  const rowH = 30;
  const height = V8_AUDIT.length * rowH + 20;
  const labelW = 150;
  const plotW = width - labelW - 20;
  const maxV = 80;
  const scale = plotW / maxV;

  return (
    <figure className="hatpl-chart">
      <div className="hatpl-chart-legend">
        <span><i style={{ background: "var(--accent)" }} />System's reported accuracy</span>
        <span><i style={{ background: "#96a0aa" }} />Naive "always guess up" baseline</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img"
           aria-label="Our earlier indices model's reported directional accuracy vs a naive majority-class baseline, all 16 indices -- the baseline wins in 11 of 16">
        {V8_AUDIT.map((d, i) => {
          const y = 8 + i * rowH;
          return (
            <g key={d.index}>
              <text x={0} y={y + 13} className="hatpl-chart-label" style={{ fontSize: 11 }}>{d.index}</text>
              <rect x={labelW} y={y} width={d.reported * scale} height={9} className="hatpl-chart-bar" />
              <rect x={labelW} y={y + 11} width={d.naive * scale} height={9} className="hatpl-chart-bar-b" />
              {!d.beatsNaive && (
                <text x={labelW + Math.max(d.reported, d.naive) * scale + 6} y={y + 15}
                      className="hatpl-chart-value" style={{ fill: "#c9453f", fontSize: 10 }}>
                  baseline wins
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <figcaption>
        Our own earlier indices model's reported directional accuracy against a model-free "the market usually
        goes up" baseline, for all 16 indices it claimed to forecast. In 11 of 16, the baseline — no model, no
        features, nothing — does as well or better.
      </figcaption>
      <div className="hatpl-asof">
        Frozen case-study audit, not a recomputed figure. Source: the predecessor system's own saved validation
        artifacts, read directly.
      </div>
    </figure>
  );
}

const CANONICAL = "https://tickerpicker.in/market-myths?evidence=index-direction";

export function IndexDirectionArticlePage({ onBack }: { onBack: () => void }) {
  useSeoTags({
    title: "We tried four different ways to predict the market. Then we audited our own old model. — Tickerpicker",
    description:
      "Four independent tests found no way to forecast which way an NSE index moves next. Then we checked our " +
      "own earlier system's claimed 62.7% accuracy against a model that does nothing at all.",
    canonical: CANONICAL,
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "We tried four different ways to predict the market. Then we audited our own old model.",
      url: CANONICAL,
      publisher: { "@type": "Organization", name: "Tickerpicker" },
      isPartOf: { "@type": "WebPage", name: "Market Myths: Tested", url: "https://tickerpicker.in/market-myths" },
    },
  });

  return (
    <div className="hatpl-shell">
      <nav className="hatpl-nav">
        <a href="/" className="hatpl-brand"><span className="dot" />TickerPicker</a>
        <div className="mm-evid-crumbs">
            <a href="/">Home</a>
            <span className="mm-crumb-sep">/</span>
            <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
          </div>
      </nav>

      <article className="hatpl-article">
        <div className="hatpl-kicker">Market Myths: Tested — index direction</div>
        <h1>We tried four different ways to predict the market. Then we audited our own old model.</h1>

        <p>
          Can you tell which way NIFTY, SENSEX, or a sector index is headed over the next few weeks, using
          price, volume, flow, or calendar data anyone can get? We went after this from four separate angles,
          and then — because we once built and shipped a system that claimed to already answer yes — we went
          back and checked that system. Neither effort found an edge.
        </p>

        <h2>Four attempts, four nothings</h2>

        <p>
          The first pass used the same technical and macro features that work well for individual stocks in
          this project, tested against NIFTY and SENSEX at three horizons. One result nominally cleared the
          bar — then failed every follow-up check we threw at it: it moved wildly between the sample it was
          found in and the full dataset, flipped sign across different stretches of history, and didn't show up
          at all in SENSEX, an index that tracks NIFTY on 95% of trading days. A one-in-twenty fluke dressed up
          as a finding, caught by asking it to repeat itself.
        </p>

        <p>
          The second pass widened the net — market breadth, foreign-investor flow, India VIX, regime-conditional
          effects, combined ensembles, 322 tests in total. Four candidates cleared initial significance; all
          four failed when we actually tried trading them out-of-sample, performing at or below chance. The
          third pass went looking for calendar and cyclical structure — day-of-week, month-end, periodicity, the
          specific idea that a down week predicts an up week — across 168 tests. Nothing survived. The fourth
          asked a different question: not whether any single index is predictable, but whether indices rotate
          predictably relative to each other, with or without foreign-investor sector flow data leading the
          rotation. Of 26 tests on the flow-leads-rotation question alone, zero survived correction, and the
          results showed no consistent direction at all — the signature of noise, not a hidden pattern.
        </p>

        <h2>The part that actually hurt: auditing ourselves</h2>

        <p>
          Here's the harder thing we did. An earlier phase of this project built and deployed its own indices
          model — four different machine-learning approaches, around 130 features per index, a live dashboard —
          and it reported 62.7% average directional accuracy across 16 indices. We went back into that system's
          own code and its own saved results and checked exactly how that number was produced.
        </p>

        <p>
          What we found: a single train/test split, not the walk-forward testing the codebase itself contained
          but never actually used for the headline number. Four models tried per index, the best one reported,
          with no penalty for having picked the winner after the fact. And no comparison, anywhere, to how often
          the market simply goes up on its own.
        </p>

        <V8AuditChart />

        <blockquote className="hatpl-pullquote">
          Eleven of sixteen indices' reported "directional accuracy" is lower than doing nothing at all.
        </blockquote>

        <p>
          Once we built that comparison — using the system's own saved predictions and test windows, nothing
          re-derived — eleven of sixteen indices came in behind a model-free baseline that just assumes the
          market keeps doing what it usually does. The five that did beat the baseline won by one to four
          percentage points, on a single untested window, from a best-of-four selection — structurally the same
          shape as every false positive our newer framework catches elsewhere in this investigation. This isn't
          a story about anyone acting in bad faith; the walk-forward code existed in that system, it just was
          never the code that produced the published number. That gap — between a correct method existing
          somewhere in a codebase and a correct method actually being the one that ran — is exactly what this
          whole exercise was built to close.
        </p>

        <p>
          Two candidates came close enough to flag without calling them findings. A low-market-breadth regime
          showed mild subsequent outperformance in 6 of 7 indices tested — the same direction every time, which
          is the kind of cross-index agreement worth a dedicated follow-up — but no single index cleared the
          corrected bar on its own. And a negative March effect in NIFTY AUTO replicated independently in both
          halves of a ten-year sample, the most-replicated calendar candidate we found, and still fell short of
          the full-sample threshold. Neither is a result. Both are named here so a future, more highly powered
          study knows where to look.
        </p>

        <h2>So is our method just too strict to find anything?</h2>

        <p>
          No — and we can show that directly, because the identical toolkit, applied to individual stocks
          instead of whole indices, finds real signal. Of 15 stock-return models we've built by segment
          (sector, liquidity tier, market cap), every one we could fully walk-forward test passed with every
          fold positive. Among the liquidity-based segments alone, five independently-built models produced a
          clean, monotonic gradient — the least liquid stocks showing the strongest signal, the most liquid
          showing the weakest, in exactly the order illiquidity theory predicts. Our tools clearly can find a
          real effect when one exists. They just don't find one in daily index-level direction.
        </p>

        <p>
          The likely reason is structural, not a failure of effort: a daily index value averages hundreds of
          individually-idiosyncratic stocks together, and whatever cross-sectional structure drives the
          stock-level result is washed out in the process of forming that single number. We don't try to call
          index direction. We forecast a volatility range instead, centered on no directional view — which is
          what the evidence here actually supports.
        </p>

        <hr className="hatpl-rule" />

        <details className="hatpl-numbers">
          <summary>The full numbers</summary>
          <h3>Investigation 1 — blessed feature pool, NIFTY &amp; SENSEX, 3 horizons</h3>
          <table>
            <tbody>
              <tr><td>Tests run</td><td>6 (index &times; horizon), return framing</td></tr>
              <tr><td>Nominally cleared</td><td>1 (NIFTY, h=30, pct_52w_high)</td></tr>
              <tr><td>Survived follow-up checks</td><td>0 — unstable across sub-periods, absent in SENSEX</td></tr>
            </tbody>
          </table>
          <h3>Investigation 2 — expanded search, regimes, ensembles (h=10)</h3>
          <table>
            <tbody>
              <tr><td>New-feature tests</td><td>322 &mdash; 4 nominal clears, 0 survived 8-fold walk-forward</td></tr>
              <tr><td>Regime-conditional tests</td><td>16 &mdash; 0 of 16 survive correction (Bonferroni &alpha;=0.00238)</td></tr>
              <tr><td>Combined-ensemble tests</td><td>7 indices &mdash; 6 of 7 worse than their own base rate OOS</td></tr>
            </tbody>
          </table>
          <h3>Investigation 3 — calendar &amp; cyclical structure (168 tests)</h3>
          <table>
            <tbody>
              <tr><td>Calendar effects</td><td>133 tests &mdash; 0 survive correction</td></tr>
              <tr><td>Autocorrelation</td><td>21 tests &mdash; 1 nominal clear (NIFTY lag-5), failed walk-forward (1/8 folds)</td></tr>
              <tr><td>Spectral/periodogram</td><td>7 tests &mdash; no excess power vs. random-walk null anywhere (p=0.38&ndash;0.94)</td></tr>
              <tr><td>"Down week predicts up week"</td><td>7 tests &mdash; 0 survive, none even nominally significant</td></tr>
            </tbody>
          </table>
          <h3>Investigation 4 — cross-sectional rank rotation &amp; FPI flow</h3>
          <table>
            <tbody>
              <tr><td>Rank persistence (pooled Spearman)</td><td>+0.019, p=0.183 &mdash; no detectable persistence or reversal</td></tr>
              <tr><td>FPI-flow-leads-rotation tests</td><td>26, Bonferroni &alpha;=0.0019 &mdash; 0 survive, best nominal p=0.018</td></tr>
              <tr><td>Live follow-on</td><td>the rank-structure half now runs weekly &mdash; see <a href="/index-rotation">/index-rotation</a></td></tr>
            </tbody>
          </table>
          <h3>Positive control — stock-level segments</h3>
          <table>
            <tbody>
              <tr><td>Segment models passing full walk-forward</td><td>15 of 15 testable, all folds positive</td></tr>
              <tr><td>Liquidity-gradient mean IC range</td><td>+0.056 (top-liquidity) to +0.158 (illiquid) &mdash; monotonic</td></tr>
            </tbody>
          </table>
          <p className="hatpl-source">
            Scripts: <code>backend/index_direction_pilot.py</code> (Investigation 1),
            <code>backend/index_direction_hunt.py</code> (Investigation 2),
            <code>backend/index_cyclicity_hunt.py</code> (Investigation 3),
            <code>backend/index_rotation_hunt.py</code> (Investigation 4). Section 5's case study audits a
            separate, earlier, non-merged codebase directly — every number quoted from that system's own saved
            validation artifacts, none re-derived or estimated. Full detail, methodology, and every table:{" "}
            <code>docs/research/nse_index_direction_nonforecastability.md</code>.
          </p>
        </details>

        <div className="mm-evid-footnav">
          <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
        </div>
      </article>
    </div>
  );
}
